import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Code, ArrowLeft, Download, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';

const PaymentHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTransactions();
  }, [user, navigate]);

  useEffect(() => {
    filterTransactions();
  }, [transactions, statusFilter]);

  const fetchTransactions = async () => {
    try {
      const response = await api.get('/payment/transactions');
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    if (statusFilter === 'all') {
      setFilteredTransactions(transactions);
    } else {
      setFilteredTransactions(transactions.filter(t => t.payment_status === statusFilter));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Package', 'Amount', 'Currency', 'Status'];
    const csvData = filteredTransactions.map(t => [
      new Date(t.created_at).toLocaleDateString(),
      t.package_name,
      t.amount,
      t.currency,
      t.payment_status
    ]);

    const csv = [headers, ...csvData].map(row => row.join(',')).join('\\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported to CSV');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <Code className="w-6 h-6 text-violet-600" />
              <span className="text-lg font-bold text-slate-900">CodeITT</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-6xl">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Payment History</h1>
          <p className="text-slate-600">View and manage all your payment transactions</p>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-xl p-4 mb-6 border border-slate-200">
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-slate-600" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-600">
                Showing {filteredTransactions.length} of {transactions.length}
              </span>
            </div>
            <Button
              onClick={exportToCSV}
              variant="outline"
              disabled={filteredTransactions.length === 0}
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Transactions List */}
        {filteredTransactions.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">💳</span>
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No transactions found</h3>
            <p className="text-slate-600 mb-6">
              {statusFilter !== 'all' 
                ? `No ${statusFilter} transactions to display` 
                : 'You haven\'t made any payments yet'}
            </p>
            <Button onClick={() => navigate('/payment')} className="bg-violet-600 hover:bg-violet-700">
              Subscribe to Classes
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow"
                data-testid={`transaction-${transaction.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{transaction.package_name}</h3>
                        <p className="text-sm text-slate-600">
                          {new Date(transaction.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      {getStatusBadge(transaction.payment_status)}
                    </div>
                    
                    {transaction.metadata && (
                      <div className="flex gap-4 text-sm text-slate-600 mt-2">
                        {transaction.metadata.classes && (
                          <span>📚 {transaction.metadata.classes} classes</span>
                        )}
                        {transaction.metadata.hours && (
                          <span>⏰ {transaction.metadata.hours} hours</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="text-2xl font-bold text-slate-900">
                      ${transaction.amount}
                      <span className="text-sm font-normal text-slate-600 ml-1">
                        {transaction.currency.toUpperCase()}
                      </span>
                    </div>
                    {transaction.payment_status === 'paid' && (
                      <span className="text-xs text-emerald-600">✓ Payment Successful</span>
                    )}
                    {transaction.payment_status === 'pending' && (
                      <span className="text-xs text-yellow-600">⏳ Processing...</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {transactions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <p className="text-slate-600 text-sm mb-1">Total Spent</p>
              <p className="text-3xl font-bold text-violet-600">
                ${transactions.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <p className="text-slate-600 text-sm mb-1">Successful Payments</p>
              <p className="text-3xl font-bold text-emerald-600">
                {transactions.filter(t => t.payment_status === 'paid').length}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <p className="text-slate-600 text-sm mb-1">Total Transactions</p>
              <p className="text-3xl font-bold text-slate-900">
                {transactions.length}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
